import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { environment } from 'src/environments/environment';
import type { Form } from '../../interfaces/form.interface';

@Component({
    selector: 'app-reactive-form-signature',
    standalone: false,
    templateUrl: './form-signature.component.html',
    styleUrls: ['./form-signature.component.scss'],
})
export class ReactiveFormSignatureComponent<T> {
    @Input({ required: true }) public form!: Form<T>;
    @Input({ required: true }) public model!: FormGroup;
    @Input() public serverUrl = environment.serverUrl;

    @Output() public signatureFile = new EventEmitter<File>();
    @Output() public signatureTempImage = new EventEmitter<string>();

    public signaturePreviewFailedSource = '';

    public get signatureField(): string {
        return this.form.signatureConfiguration?.field ?? 'signature_img';
    }

    public get signatureFolder(): string {
        return this.form.signatureConfiguration?.folder ?? 'fitosanitarios';
    }

    public get signatureValue(): string {
        return String(this.model.get(this.signatureField)?.value ?? '').trim();
    }

    public get signatureSrc(): string {
        const value = this.signatureValue;

        if (value) {
            return this.resolveSignatureValueSource(value);
        }

        return this.getExistingSignatureSource();
    }

    public get hasSignaturePreview(): boolean {
        const source = this.signatureSrc;

        return source !== '' && source !== this.signaturePreviewFailedSource;
    }

    public onSignatureImageError(): void {
        this.signaturePreviewFailedSource = this.signatureSrc;
    }

    public onSignatureImageLoad(): void {
        if (this.signaturePreviewFailedSource === this.signatureSrc) {
            this.signaturePreviewFailedSource = '';
        }
    }

    private resolveSignatureValueSource(value: string): string {
        if (this.isDirectImageSource(value)) {
            return value;
        }

        return `${this.getServerUrl()}images/${this.signatureFolder}/${value}`;
    }

    private getExistingSignatureSource(): string {
        const existingImageConfiguration = this.form.signatureConfiguration?.existingImageConfiguration;

        if (!existingImageConfiguration) {
            return '';
        }

        const idField = existingImageConfiguration.idField ?? 'id';
        const recordId = String(this.model.get(idField)?.value ?? '').trim();

        if (!recordId) {
            return '';
        }

        const extension = existingImageConfiguration.extension ?? '';

        return `${this.getServerUrl()}${existingImageConfiguration.pathPrefix}${recordId}${extension}`;
    }

    private getServerUrl(): string {
        return this.serverUrl || environment.serverUrl;
    }

    private isDirectImageSource(value: string): boolean {
        return value.startsWith('data:') || value.startsWith('http://') || value.startsWith('https://') || value.startsWith('/');
    }
}
